import vaultIllustration from "@src/packages/moon-ui/assets/images/cryo-vault-fridge.png";
import { DocumentFileDropZone } from "@src/packages/document-vault/DocumentFileDropZone";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  View,
  Text,
  ScrollView,
  Pressable,
  Image,
  Platform,
  type ImageSourcePropType,
} from "react-native";
import {
  Snowflake,
  MapPin,
  Calendar,
  Shield,
  ChevronRight,
  Droplet,
  Egg,
  FileText,
  AlertCircle,
  Info,
  Plus,
  Sparkles,
  Truck,
  Heart,
  type LucideIcon,
} from "lucide-react-native";
import type { NativeStackScreenProps } from "@react-navigation/native-stack";
import { EMBER } from "../../onboarding/theme";
import type { CareStackParamList } from "../types";
import { useSmartBack } from "../hooks/useSmartBack";
import { useCycleNavigation } from "../hooks/useCycleNavigation";
import GlassScreenLayout from "@louise/moon-ui/primitives/GlassScreenLayout";
import {
  CardSubtitleText,
  CompactActionButton,
  EmptyState,
  CardTitleText,
  IconButton,
  ListRowSkeleton,
  SectionTitle,
  ClinicalDataStatus,
  Dialog,
  DialogHeader,
  PrimaryButton,
  SecondaryButton,
  BackLink,
  MoonCheckboxBox,
  Row,
  TextActionButton,
  cardSurface,
} from "@louise/moon-ui";
import StatusBadge from "../components/StatusBadge";
import ScreenHeader from "../components/ScreenHeader";
import { OrganicMiniIcon } from "@src/packages/homepage/plan/components/OrganicMiniIcon";
import { Hero } from "@src/packages/homepage/plan/components/hero/Hero";
import { SCREEN_PADDING_TOP } from "@louise/moon-ui/theme/tokens";
import {
  getRemainingOocyteCohortCount,
  useOocyteCohorts,
  useDeleteSpecimen,
  useSpecimens,
  type OocyteCohort,
} from "@src/packages/care/cryovault/useSpecimens";
import { CONSUMING_SPECIMEN_USAGES } from "@src/packages/care/cryovault/frozenSpecimenUsages";
import { useCycles } from "@src/packages/cycles/hooks/useCycles";
import type { PickerSpecimen } from "@src/packages/care/cryovault/FrozenSpecimenSelector";
import {
  useDocuments,
  type Document,
} from "@src/packages/documents/hooks/useDocuments";
import { AddSpecimenModal } from "./care/modals/AddSpecimenModal";
import type { FrozenPrefill } from "./care/modals/MoonStartTreatmentModal";
import { SelectFrozenSpecimensModal } from "./care/modals/SelectFrozenSpecimensModal";
import { useStartTreatmentController } from "../StartTreatmentController";
import { DocumentRow } from "./documents/DocumentRow";
import { useLocale, useTranslations } from "@src/packages/i18n";
import { formatDate } from "@src/packages/i18n/formatters";
import type { Locale } from "@src/packages/i18n/locale";
import {
  CryoSpecimenDetailModal,
  buildCryoSpecimenLabel,
  mapApiOocyteCohortsToCryoDetails,
  mapApiSpecimensToCryoDetails,
  type CryoSpecimenDetail,
} from "../components/CryoSpecimenDetailModal";
import {
  computeCombinedPotential,
  computeEggPotential,
  computeEmbryoPotential,
  type CombinedPotentialStep,
  type PotentialStep,
} from "./cryoPotential";
import {
  OutcomeDotFunnelRows,
  type OutcomeDotFunnelStage,
} from "@src/packages/homepage/plan/components/OutcomeDotFunnelRows";
import {
  FunnelScopeTabs,
  type FunnelScopeTabOption,
} from "@src/packages/homepage/plan/components/FunnelScopeTabs";
import { getDevClinicalDataState } from "@src/packages/care/dev-clinical-data-state";
import {
  buildDevCryoVaultSpecimens,
  buildDevSpecimenModalSpecimens,
  getDevAddEmbryosPrefill,
  getDevCryoVaultScenario,
  shouldOpenDevAddEmbryosSurface,
} from "../dev/cryoVaultFixture";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@louise/moon-ui/primitives/Accordion";

import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";
// ── Types ──

type SpecimenType = "embryo" | "oocyte" | "sperm";

type Specimen = CryoSpecimenDetail;

/**
 * A row visible in the vault today. Includes partially-used cohorts (which
 * still hold remaining material) and rows whose members were consumed —
 * those stay listed so the patient sees her cycle history, and the per-tile
 * rendering distinguishes the used-up ones. Availability (how many can be
 * reserved for a NEW cycle) is decided elsewhere by `isInFrozenStorage`.
 */
function isVerifiedStoredSpecimen(specimen: Specimen): boolean {
  return (
    specimen.status === "stored" ||
    (specimen.oocyteRecordKind === "cohort" &&
      specimen.status === "partially_used" &&
      Boolean(specimen.facility && specimen.frozenDate))
  );
}

const DOCUMENT_CYCLE_TAG_PREFIX = "cycle:";
const DOCUMENT_SPECIMEN_TAG_PREFIX = "specimen:";

/**
 * These tags are the explicit document/entity relationships currently
 * available to the client. Category tags are intentionally not treated as
 * CryoVault links. When a record exposes multiple cycle relationships, keep
 * every persisted relationship in the document filter rather than collapsing
 * the graph to the legacy scalar projections.
 */
function getCryoDocumentRelationTags(specimens: Specimen[]): Set<string> {
  const tags = new Set<string>();
  for (const specimen of specimens) {
    tags.add(`${DOCUMENT_SPECIMEN_TAG_PREFIX}${specimen.id}`);
    const explicitCycleIds = specimen.cycleRelationships?.map(
      (relationship) => relationship.cycleId,
    ) ?? [specimen.cycleId, specimen.useCycleId, specimen.transferCycleId];
    for (const cycleId of explicitCycleIds) {
      if (cycleId) tags.add(`${DOCUMENT_CYCLE_TAG_PREFIX}${cycleId}`);
    }
  }
  return tags;
}

function isCryoRelatedDocument(
  document: Document,
  relationTags: Set<string>,
): boolean {
  return document.tags.some((tag) => relationTags.has(tag));
}

function getDocumentRowStatus(
  document: Document,
): "on-file" | "requested" | undefined {
  if (document.uploads.some((upload) => upload.uploadStatus === "pending")) {
    return "requested";
  }
  if (document.uploads.some((upload) => upload.uploadStatus === "uploaded")) {
    return "on-file";
  }
  return undefined;
}

type CryoUploadFile = {
  name: string;
  uri: string;
  mimeType?: string | null;
  objectUrl?: string;
  size?: number;
};

type FacilityInfo = {
  name: string;
  specimenCount: number;
  storageStartDate?: string | null;
  nextPaymentDate?: string | null;
  annualCostCents?: number | null;
};

type RetrievalFunnel = {
  cycleId?: string;
  cycleLabel: string;
  retrieved: number;
  mature?: number | null;
  fertilized?: number | null;
  blastocyst?: number | null;
  pgtTested?: number | null;
  euploid?: number | null;
  frozen: number;
};

type DispositionStatus = "filed" | "missing" | "expiring_soon";

// ── Config ──

const SPECIMEN_CONFIG: Record<
  SpecimenType,
  {
    singularKey: string;
    pluralKey: string;
    color: string;
    sectionIcon: LucideIcon;
  }
> = {
  embryo: {
    singularKey: "cryo_vault.specimen_type.embryo_singular",
    pluralKey: "cryo_vault.specimen_type.embryo_plural",
    color: "#38BDF8",
    sectionIcon: Sparkles,
  },
  oocyte: {
    singularKey: "cryo_vault.specimen_type.egg_singular",
    pluralKey: "cryo_vault.specimen_type.egg_plural",
    color: EMBER.colors.gold,
    sectionIcon: Egg,
  },
  sperm: {
    singularKey: "cryo_vault.specimen_type.sperm_sample_singular",
    pluralKey: "cryo_vault.specimen_type.sperm_sample_plural",
    color: "#86EFAC",
    sectionIcon: Droplet,
  },
};

const PGT_CONFIG: Record<string, { labelKey: string; color: string }> = {
  euploid: { labelKey: "cryo_vault.pgt.euploid", color: EMBER.colors.green },
  aneuploid: {
    labelKey: "cryo_vault.pgt.aneuploid",
    color: EMBER.colors.coral,
  },
  mosaic: { labelKey: "cryo_vault.pgt.mosaic", color: EMBER.colors.gold },
  untested: {
    labelKey: "cryo_vault.pgt.untested",
    color: EMBER.colors.textFaint,
  },
};

const OOCYTE_SAMPLE_IMAGE =
  require("../../assets/images/3d/oocyte-sample.png") as ImageSourcePropType;

const MAX_VISIBLE_OOCYTE_UNITS = 40;

// ── Components ──

function getOriginalFrozenOocyteCount(specimen: Specimen): number {
  if (specimen.type !== "oocyte") return 0;
  if (specimen.oocyteRecordKind !== "cohort") return 1;
  return Math.max(0, specimen.numberFrozen ?? 1);
}

function getRemainingFrozenOocyteCount(specimen: Specimen): number {
  if (specimen.type !== "oocyte") return 0;
  return getRemainingOocyteCohortCount(
    specimen.oocyteRecordKind === "cohort" ? specimen.numberFrozen : null,
    specimen.numberThawed,
    1,
  );
}

function getSpecimenInventoryCount(specimen: Specimen): number {
  if (specimen.type === "oocyte") {
    return getRemainingFrozenOocyteCount(specimen);
  }
  return 1;
}

function getSpecimenInventoryTotal(specimens: Specimen[]): number {
  return specimens.reduce(
    (sum, specimen) => sum + getSpecimenInventoryCount(specimen),
    0,
  );
}

/**
 * The tile title is the cohort's identity (its label) — counts of frozen,
 * thawed and fertilized live in the subtitle so the header stays legible at
 * a glance and the state summary can grow without wrapping the title.
 */
function getOocyteCohortInventoryTitle(
  _t: ReturnType<typeof useTranslations>,
  specimen: Specimen,
): string {
  return specimen.label;
}

/**
 * The state summary that sits under the cohort title: frozen count when the
 * cohort is untouched, adds thawed then fertilized as those numbers land.
 * Returns null when there is nothing to say (a fresh cohort with zero of
 * everything — that state never happens in practice but the guard keeps the
 * component from rendering an empty line).
 */
function getOocyteCohortStateSubtitle(
  t: ReturnType<typeof useTranslations>,
  specimen: Specimen,
): string | null {
  const remaining = getRemainingFrozenOocyteCount(specimen);
  const thawed = specimen.numberThawed ?? 0;
  const fertilized = specimen.fertilizedCount ?? 0;
  const parts: string[] = [];
  const plural = (base: string, count: number) =>
    t(count === 1 ? `${base}_one` : `${base}_other`, {
      params: { count: String(count) },
    });
  if (remaining > 0) {
    parts.push(plural("cryo_vault.cohort_state.frozen_count", remaining));
  }
  if (thawed > 0) {
    parts.push(plural("cryo_vault.cohort_state.thawed_count", thawed));
  }
  if (fertilized > 0) {
    parts.push(plural("cryo_vault.cohort_state.fertilized_count", fertilized));
  }
  return parts.length > 0 ? parts.join(" · ") : null;
}

function CryoSummary({ specimens }: { specimens: Specimen[] }) {
  const t = useTranslations();
  const verifiedSpecimens = specimens.filter(isVerifiedStoredSpecimen);
  const embryoCount = verifiedSpecimens.filter(
    (s) => s.type === "embryo",
  ).length;
  const eggCount = getSpecimenInventoryTotal(
    verifiedSpecimens.filter((s) => s.type === "oocyte"),
  );
  const spermCount = verifiedSpecimens.filter((s) => s.type === "sperm").length;
  const total = embryoCount + eggCount + spermCount;
  const facilityCount = new Set(
    verifiedSpecimens.map((s) => s.facility).filter(Boolean),
  ).size;

  if (total === 0) return null;

  const breakdownParts: string[] = [];
  if (embryoCount > 0) {
    breakdownParts.push(
      `${embryoCount} ${
        embryoCount === 1
          ? t("cryo_vault.specimen_type.embryo_singular")
          : t("cryo_vault.specimen_type.embryo_plural")
      }`,
    );
  }
  if (eggCount > 0) {
    breakdownParts.push(
      `${eggCount} ${
        eggCount === 1
          ? t("cryo_vault.specimen_type.egg_singular")
          : t("cryo_vault.specimen_type.egg_plural")
      }`,
    );
  }
  if (spermCount > 0) {
    breakdownParts.push(
      `${spermCount} ${
        spermCount === 1
          ? t("cryo_vault.specimen_type.sperm_sample_singular")
          : t("cryo_vault.specimen_type.sperm_sample_plural")
      }`,
    );
  }
  const breakdown = breakdownParts.join(" · ");
  const facilityLabel =
    facilityCount === 1
      ? t("cryo_vault.hero.facility_one")
      : t("cryo_vault.hero.facility_other", {
          params: { count: String(facilityCount) },
        });

  return (
    <Hero
      eyebrow={{ label: t("cryo_vault.hero.eyebrow"), icon: Snowflake }}
      title={
        total === 1
          ? t("cryo_vault.hero.title_singular", {
              params: { count: String(total) },
            })
          : t("cryo_vault.hero.title_plural", {
              params: { count: String(total) },
            })
      }
      summary={`${breakdown} ${facilityLabel}`}
    />
  );
}

/** Specimen icon — real embryo image if present, otherwise organic illustration */
function SpecimenIcon({
  specimen,
  size,
}: {
  specimen: Specimen;
  size: number;
}) {
  if (specimen.type === "embryo") {
    const realImageUrl = specimen.mediaUrls?.[0];
    if (realImageUrl) {
      return (
        <Image
          source={{ uri: realImageUrl }}
          style={{
            width: size,
            height: size,
            borderRadius: size / 3,
          }}
          resizeMode="contain"
        />
      );
    }
    return <OrganicMiniIcon kind="genetics" size={size} />;
  }
  if (specimen.type === "oocyte") {
    return <OrganicMiniIcon kind="eggs" size={size} />;
  }
  return <OrganicMiniIcon kind="sperm" size={size} />;
}

/** Vertical specimen card for 2-column grid */
function SpecimenTile({ specimen }: { specimen: Specimen }) {
  const t = useTranslations();
  const { locale } = useLocale();
  const pgt = specimen.pgtResult ? PGT_CONFIG[specimen.pgtResult] : null;
  const frozenFormatted = formatSpecimenDate(specimen.frozenDate, locale);
  const subtitleParts: string[] = [];
  const storageStatus = getStorageStatusLabel(t, specimen);
  if (storageStatus) subtitleParts.push(storageStatus);
  if (specimen.grade) {
    subtitleParts.push(
      t("cryo_vault.grade_prefix", { params: { grade: specimen.grade } }),
    );
  }
  if (specimen.motility) {
    subtitleParts.push(
      t("cryo_vault.motility_suffix", { params: { value: specimen.motility } }),
    );
  }
  if (frozenFormatted) {
    subtitleParts.push(frozenFormatted);
  }
  const subtitle = subtitleParts.join(" · ");

  return (
    <View
      style={{
        flex: 1,
        ...cardSurface(),
        minHeight: 118,
        padding: 14,
        justifyContent: "space-between",
      }}
    >
      <View
        style={{
          flexDirection: "row",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 8,
        }}
      >
        <SpecimenIcon specimen={specimen} size={36} />
        <ChevronRight
          size={19}
          color={EMBER.colors.textLight}
          strokeWidth={1.7}
        />
      </View>

      <View
        style={{
          flexDirection: "row",
          alignItems: "flex-end",
          justifyContent: "space-between",
          gap: 8,
        }}
      >
        <View style={{ flex: 1, minWidth: 0 }}>
          <CardTitleText numberOfLines={1}>{specimen.label}</CardTitleText>
          {subtitle ? (
            <CardSubtitleText numberOfLines={1}>{subtitle}</CardSubtitleText>
          ) : null}
        </View>
        {pgt ? (
          <StatusBadge
            label={t(pgt.labelKey)}
            tone={
              specimen.pgtResult === "euploid"
                ? "green"
                : specimen.pgtResult === "aneuploid"
                  ? "coral"
                  : specimen.pgtResult === "mosaic"
                    ? "gold"
                    : "neutral"
            }
          />
        ) : null}
      </View>
    </View>
  );
}

function formatSpecimenDate(
  raw: string | null | undefined,
  locale: Locale,
): string | null {
  if (!raw) return null;
  const datePart = raw.split("T")[0];
  const parsed = new Date(`${datePart}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return raw;
  return formatDate(parsed, locale, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

/** 2-column grid of specimen tiles with optional batch indicators */
function SpecimenGrid({
  items,
  onSelect,
}: {
  items: Specimen[];
  onSelect: (id: string) => void;
}) {
  const capturePress = usePressCapture("SpecimenGrid");
  // Check if there are multiple batches (for batch dot indicator)
  const batchIds = [
    ...new Set(items.map((s) => s.batchId).filter(Boolean)),
  ] as string[];
  const showBatchDots = batchIds.length > 1;
  const batchColorMap: Record<string, string> = {};
  const batchColors = [
    EMBER.colors.cryo,
    EMBER.colors.gold,
    EMBER.colors.green,
    EMBER.colors.coral,
  ];
  batchIds.forEach((id, i) => {
    batchColorMap[id] = batchColors[i % batchColors.length] ?? "#38BDF8";
  });

  return (
    <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 10 }}>
      {items.map((specimen) => (
        <Pressable
          key={specimen.id}
          onPress={() => {
            capturePress(pressLabel(specimen.label), "select_specimen");
            onSelect(specimen.id);
          }}
          style={{
            flexGrow: 1,
            flexBasis: "22%",
            minWidth: 130,
            position: "relative",
          }}
        >
          <SpecimenTile specimen={specimen} />
          {/* Batch dot */}
          {showBatchDots && specimen.batchId && (
            <View
              style={{
                position: "absolute",
                top: 8,
                right: 8,
                width: 6,
                height: 6,
                borderRadius: 3,
                backgroundColor:
                  batchColorMap[specimen.batchId] ?? EMBER.colors.textFaint,
              }}
            />
          )}
        </Pressable>
      ))}
    </View>
  );
}

type SpecimenDateBatch = {
  /** Explicit cohort/batch identity. Individual rows never merge by date. */
  identity: string;
  frozenDate: string | null;
  items: Specimen[];
};

type SpecimenFacilityGroup = {
  facility: string;
  batches: SpecimenDateBatch[];
};

function getSpecimenCountLabel(
  t: ReturnType<typeof useTranslations>,
  specimenType: SpecimenType,
  count: number,
): string {
  const config = SPECIMEN_CONFIG[specimenType];
  return `${count} ${count === 1 ? t(config.singularKey) : t(config.pluralKey)}`;
}

function getStorageStatusLabel(
  t: ReturnType<typeof useTranslations>,
  specimen: Specimen,
): string | null {
  if (specimen.status === "storage_pending") {
    return t("cryo_vault.modal.storage_pending");
  }
  if (specimen.status === "storage_unverified") {
    return t("cryo_vault.modal.storage_unverified");
  }
  return null;
}

function getNestedSpecimenDetailRows(
  t: ReturnType<typeof useTranslations>,
  specimen: Specimen,
): string[] {
  const storageStatus = getStorageStatusLabel(t, specimen);
  if (specimen.type === "embryo") {
    const rows: string[] = storageStatus ? [storageStatus] : [];
    if (specimen.grade) {
      rows.push(
        t("cryo_vault.grade_prefix", { params: { grade: specimen.grade } }),
      );
    }

    const outcomeParts: string[] = [];
    const pgt = specimen.pgtResult ? PGT_CONFIG[specimen.pgtResult] : null;
    if (pgt) {
      outcomeParts.push(t(pgt.labelKey));
    }
    if (specimen.transferCandidate) {
      outcomeParts.push(t("cryo_vault.top_pick"));
    }
    if (outcomeParts.length > 0) {
      rows.push(outcomeParts.join(" · "));
    }
    return rows;
  }

  if (specimen.type === "sperm") {
    const rows: string[] = storageStatus ? [storageStatus] : [];
    if (specimen.motility) {
      rows.push(
        t("cryo_vault.motility_suffix", {
          params: { value: specimen.motility },
        }),
      );
    }

    const labParts = [specimen.concentration, specimen.morphology].filter(
      (part): part is string => Boolean(part),
    );
    if (labParts.length > 0) {
      rows.push(labParts.join(" · "));
    }
    return rows;
  }

  return storageStatus ? [storageStatus] : [];
}

/**
 * Individual specimen rows from the same retrieval — same cycle, same freeze
 * date, same facility — belong together, exactly as a cohort would. The batch
 * endpoint writes them as N unlinked rows because the server has no cohort
 * write path yet; keying them by their shared provenance re-groups them for
 * the render so she does not see the same intake spread across N tiles.
 *
 * Only oocytes: sperm vials and embryos are individually meaningful units and
 * do not collapse into one another.
 */
function getSpecimenBatchIdentity(specimen: Specimen): string {
  if (specimen.batchId) return `batch:${specimen.batchId}`;
  // Same-cycle same-date individuals sit under one batch header rather than
  // one tile-per-row. For oocytes it also drives the virtual-cohort tile
  // (which collapses to a single "N Eggs" record at data-prep time); for
  // embryos and sperm it only groups the tiles under a shared subtitle —
  // both stay individually meaningful (embryo grade / PGT / transfer
  // candidate; sperm vial's own motility and concentration numbers) so a
  // collapse would lose information she needs to make selection decisions.
  //
  // Specimens recorded directly through the CryoVault intake carry no
  // cycleId (they were not saved against a treatment cycle), so falling
  // back to `(facility, frozenDate)` alone lets them still group under one
  // batch instead of showing one tile per row.
  if (specimen.oocyteRecordKind !== "cohort" && specimen.frozenDate) {
    const provenanceKey = specimen.cycleId
      ? `${specimen.cycleId}:${specimen.frozenDate}`
      : `no-cycle:${specimen.facility ?? ""}:${specimen.frozenDate}`;
    if (specimen.type === "oocyte") {
      return `virtual-oocyte-batch:${provenanceKey}`;
    }
    if (specimen.type === "embryo") {
      return `virtual-embryo-batch:${provenanceKey}`;
    }
    if (specimen.type === "sperm") {
      return `virtual-sperm-batch:${provenanceKey}`;
    }
  }
  return `specimen:${specimen.id}`;
}

function groupSpecimensByFacilityAndDate(
  items: Specimen[],
): SpecimenFacilityGroup[] {
  const facilityGroups = items.reduce<SpecimenFacilityGroup[]>(
    (acc, specimen) => {
      const facilityKey = specimen.facility ?? "";
      let group = acc.find((g) => g.facility === facilityKey);
      if (!group) {
        group = { facility: facilityKey, batches: [] };
        acc.push(group);
      }

      const identity = getSpecimenBatchIdentity(specimen);
      const batchKey = specimen.frozenDate ?? null;
      const batch = group.batches.find((b) => b.identity === identity);
      if (batch) {
        batch.items.push(specimen);
      } else {
        group.batches.push({
          identity,
          frozenDate: batchKey,
          items: [specimen],
        });
      }

      return acc;
    },
    [],
  );

  facilityGroups.sort((a, b) => a.facility.localeCompare(b.facility));
  facilityGroups.forEach((group) => {
    group.batches.sort((a, b) =>
      (b.frozenDate ?? "").localeCompare(a.frozenDate ?? ""),
    );
  });

  return facilityGroups;
}

function OocyteUnitGrid({ specimen }: { specimen: Specimen }) {
  const originalFrozen = getOriginalFrozenOocyteCount(specimen);
  const remaining = getRemainingFrozenOocyteCount(specimen);
  const fertilized = Math.min(
    Math.max(0, specimen.fertilizedCount ?? 0),
    originalFrozen,
  );
  const thawed = Math.min(
    Math.max(0, specimen.numberThawed ?? 0),
    originalFrozen,
  );
  // The fertilized count is a subset of the thawed count (you cannot
  // fertilize an egg that was never thawed). Guard against a stale record
  // where the two disagree so we never draw more fertilized icons than
  // thawed ones.
  const thawedOnly = Math.max(0, thawed - fertilized);
  const visibleCount = Math.min(originalFrozen, MAX_VISIBLE_OOCYTE_UNITS);
  const hiddenCount = Math.max(0, originalFrozen - visibleCount);

  const overflowChip =
    hiddenCount > 0 ? (
      <View
        key="overflow"
        style={{
          minWidth: 34,
          height: 21,
          borderRadius: 10,
          backgroundColor: "rgba(255,255,255,0.58)",
          borderWidth: 1,
          borderColor: "rgba(92,52,32,0.12)",
          alignItems: "center",
          justifyContent: "center",
          paddingHorizontal: 6,
        }}
      >
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 10.5,
            lineHeight: 12,
            fontWeight: "800",
            color: EMBER.colors.textLight,
          }}
        >
          +{hiddenCount}
        </Text>
      </View>
    ) : null;

  const frozenEgg = (key: string) => (
    <Image
      key={key}
      source={OOCYTE_SAMPLE_IMAGE}
      resizeMode="contain"
      style={{ width: 24, height: 29, opacity: 0.96 }}
    />
  );
  const fadedEgg = (key: string) => (
    <Image
      key={key}
      source={OOCYTE_SAMPLE_IMAGE}
      resizeMode="contain"
      style={{ width: 24, height: 29, opacity: 0.32 }}
    />
  );

  // A faded egg with a small warm sparkle glyph in the top-right corner.
  // No background pill — the sparkle is drawn directly on the tile using
  // the Hero card's warm brown.
  const fertilizedEgg = (key: string) => (
    <View
      key={key}
      style={{
        width: 24,
        height: 29,
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Image
        source={OOCYTE_SAMPLE_IMAGE}
        resizeMode="contain"
        style={{ width: 24, height: 29, opacity: 0.32 }}
      />
      <Sparkles
        size={8}
        color={EMBER.colors.warm}
        strokeWidth={1.6}
        style={{
          position: "absolute",
          top: 1,
          right: 0,
        }}
      />
    </View>
  );

  const kinds: ("frozen" | "thawed" | "fertilized")[] = [];
  for (let i = 0; i < remaining && kinds.length < visibleCount; i++) {
    kinds.push("frozen");
  }
  for (let i = 0; i < thawedOnly && kinds.length < visibleCount; i++) {
    kinds.push("thawed");
  }
  for (let i = 0; i < fertilized && kinds.length < visibleCount; i++) {
    kinds.push("fertilized");
  }

  return (
    <View
      style={{
        flexDirection: "row",
        flexWrap: "wrap",
        gap: 2,
        alignItems: "center",
      }}
    >
      {kinds.map((kind, index) => {
        const key = `${specimen.id}-egg-unit-${index}`;
        if (kind === "frozen") return frozenEgg(key);
        if (kind === "thawed") return fadedEgg(key);
        return fertilizedEgg(key);
      })}
      {overflowChip}
    </View>
  );
}

function NestedSpecimenTile({
  specimen,
  onPress,
}: {
  specimen: Specimen;
  onPress: () => void;
}) {
  const capturePress = usePressCapture("NestedSpecimenTile");
  const t = useTranslations();
  const detailRows = getNestedSpecimenDetailRows(t, specimen).slice(0, 2);
  const isCompactOocyte = specimen.type === "oocyte";
  const isOocyteCohort =
    specimen.type === "oocyte" && specimen.oocyteRecordKind === "cohort";
  const oocyteCohortTitle = isOocyteCohort
    ? getOocyteCohortInventoryTitle(t, specimen)
    : specimen.label;
  const accessibilityLabel = [oocyteCohortTitle, ...detailRows].join(", ");

  if (isOocyteCohort) {
    const consumptionSubtitle = getOocyteCohortStateSubtitle(t, specimen);
    return (
      <Pressable
        onPress={() => {
          capturePress(pressLabel(accessibilityLabel), "cryo_vault_action");
          onPress?.();
        }}
        accessibilityRole="button"
        accessibilityLabel={accessibilityLabel}
        style={({ pressed }) => ({
          ...cardSurface({ variant: "panel" }),
          width: "100%",
          padding: 12,
          gap: 10,
          opacity: pressed ? 0.82 : 1,
        })}
      >
        <View
          style={{
            flexDirection: "row",
            alignItems: "center",
            gap: 9,
          }}
        >
          <SpecimenIcon specimen={specimen} size={28} />
          <View style={{ flex: 1, minWidth: 0 }}>
            <Text
              className="font-dm-sans"
              numberOfLines={1}
              adjustsFontSizeToFit
              minimumFontScale={0.72}
              style={{
                fontSize: 13,
                lineHeight: 16,
                fontWeight: "800",
                color: EMBER.colors.text,
              }}
            >
              {oocyteCohortTitle}
            </Text>
            {consumptionSubtitle ? (
              <Text
                className="font-dm-sans"
                numberOfLines={1}
                style={{
                  marginTop: 2,
                  fontSize: 11,
                  lineHeight: 14,
                  fontWeight: "600",
                  color: EMBER.colors.textLight,
                }}
              >
                {consumptionSubtitle}
              </Text>
            ) : null}
          </View>
          <ChevronRight
            size={17}
            color={EMBER.colors.textLight}
            strokeWidth={1.7}
          />
        </View>
        <OocyteUnitGrid specimen={specimen} />
      </Pressable>
    );
  }

  if (isCompactOocyte) {
    return (
      <Pressable
        onPress={() => {
          capturePress(pressLabel(accessibilityLabel), "cryo_vault_action");
          onPress();
        }}
        accessibilityRole="button"
        accessibilityLabel={accessibilityLabel}
        style={({ pressed }) => ({
          ...cardSurface({ variant: "panel" }),
          width: 58,
          height: 62,
          paddingHorizontal: 6,
          paddingVertical: 7,
          alignItems: "center",
          justifyContent: "center",
          gap: 4,
          opacity: pressed ? 0.78 : 1,
        })}
      >
        <SpecimenIcon specimen={specimen} size={24} />
        <Text
          className="font-dm-sans"
          numberOfLines={1}
          adjustsFontSizeToFit
          minimumFontScale={0.72}
          style={{
            maxWidth: "100%",
            fontSize: 10.5,
            lineHeight: 12,
            fontWeight: "800",
            color: EMBER.colors.text,
            textAlign: "center",
          }}
        >
          {specimen.label}
        </Text>
      </Pressable>
    );
  }

  return (
    <Pressable
      onPress={() => {
        capturePress(pressLabel(accessibilityLabel), "cryo_vault_action");
        onPress();
      }}
      accessibilityRole="button"
      accessibilityLabel={accessibilityLabel}
      style={({ pressed }) => ({
        ...cardSurface({ variant: "panel" }),
        width: "48%",
        minHeight: 82,
        paddingHorizontal: 10,
        paddingVertical: 10,
        justifyContent: "space-between",
        gap: 7,
        opacity: pressed ? 0.78 : 1,
      })}
    >
      <View
        style={{
          flexDirection: "row",
          alignItems: "center",
          gap: 7,
        }}
      >
        <SpecimenIcon specimen={specimen} size={24} />
        <Text
          className="font-dm-sans"
          numberOfLines={1}
          adjustsFontSizeToFit
          minimumFontScale={0.78}
          style={{
            flex: 1,
            fontSize: 12,
            lineHeight: 14,
            fontWeight: "800",
            color: EMBER.colors.text,
          }}
        >
          {specimen.label}
        </Text>
      </View>
      {detailRows.length > 0 && (
        <View style={{ gap: 2 }}>
          {detailRows.map((detail, index) => (
            <Text
              key={`${specimen.id}-detail-${index}`}
              className="font-dm-sans"
              numberOfLines={1}
              adjustsFontSizeToFit
              minimumFontScale={0.82}
              style={{
                fontSize: 10.5,
                lineHeight: 13,
                fontWeight: index === 0 ? "700" : "600",
                color: index === 0 ? EMBER.colors.text : EMBER.colors.textLight,
              }}
            >
              {detail}
            </Text>
          ))}
        </View>
      )}
    </Pressable>
  );
}

/**
 * Prefix on the synthetic id so consumers (delete guard, detail-modal action
 * gating) can recognise a virtual-cohort record without a shape check.
 */
const VIRTUAL_OOCYTE_COHORT_ID_PREFIX = "virtual-oocyte-cohort:";

export function isVirtualOocyteCohortId(
  id: string | null | undefined,
): boolean {
  return (
    typeof id === "string" && id.startsWith(VIRTUAL_OOCYTE_COHORT_ID_PREFIX)
  );
}

/**
 * When N individual oocyte rows share a retrieval — same cycle, same freeze
 * date, same facility — collapse them into one synthetic cohort record so
 * the vault, the detail modal, and the "use eggs" flow all read the same
 * intake as one thing. Same shape as `mapApiOocyteCohortsToCryoDetails`
 * output so the detail modal, tile renderer, and onPlanTreatment handler
 * cannot tell a virtual cohort apart from a real one.
 *
 * Only oocytes: sperm vials and embryos are individually meaningful units
 * and do not collapse.
 */
function buildVirtualOocyteCohorts(
  specimens: readonly Specimen[],
  getFertilizedForMembers: (memberIds: readonly string[]) => number,
): {
  virtualCohorts: Specimen[];
  memberIdsToRemove: Set<string>;
} {
  const bucketByProvenance = new Map<string, Specimen[]>();
  for (const specimen of specimens) {
    if (
      specimen.type !== "oocyte" ||
      specimen.oocyteRecordKind === "cohort" ||
      !specimen.frozenDate
    ) {
      continue;
    }
    // A consumed member stays in the cohort — the tile stays visible and
    // the detail modal shows the intake's whole history. What changes is
    // the derived count (`numberThawed` below), not the membership.
    // Eggs recorded directly through the vault intake have no cycleId; keep
    // grouping them by facility + freeze date so a single intake still
    // collapses into one virtual cohort tile. Empty facility falls into a
    // shared "no-facility" bucket so intakes without a clinic still render
    // as one cohort tile instead of N individual "Egg #k" rows.
    const provenance = specimen.cycleId
      ? `cycle:${specimen.cycleId}`
      : "no-cycle";
    const facilityKey = specimen.facility || "no-facility";
    const key = `${provenance}::${specimen.frozenDate}::${facilityKey}`;
    const bucket = bucketByProvenance.get(key) ?? [];
    bucket.push(specimen);
    bucketByProvenance.set(key, bucket);
  }

  const draftCohorts: {
    first: Specimen;
    items: Specimen[];
    thawedCount: number;
    fertilizedCount: number;
  }[] = [];
  const memberIdsToRemove = new Set<string>();
  for (const [, items] of bucketByProvenance) {
    if (items.length < 2) continue;
    const first = items[0];
    if (!first) continue;
    // Count is derived from usage events on each member (server writes one
    // event per specimen; the vault has to sum them itself since the cohort
    // record has no aggregated field the backend keeps in sync).
    const thawedCount = items.filter((item) =>
      (item.specimenUsages ?? []).some((usage) => usage.usage === "thaw"),
    ).length;
    // Fertilized is read from the consuming cycle's `fertilized_oocytes`
    // phase metric, the same source the Lab funnel already trusts. Per-
    // specimen `fertilize`/`use` writes were removed (deprecated endpoint
    // rejects them without a paired sperm id and embryo output).
    const fertilizedCount = getFertilizedForMembers(items.map((i) => i.id));
    draftCohorts.push({ first, items, thawedCount, fertilizedCount });
    for (const item of items) memberIdsToRemove.add(item.id);
  }
  // Number virtual cohorts by chronological freeze date (oldest = #1) so the
  // label she sees in the tile and the detail title reflects the order of her
  // retrievals, not the arbitrary order the first individual row ended up
  // with when it was mapped from the API response.
  draftCohorts.sort((a, b) => {
    const dateA = a.first.frozenDate ?? "";
    const dateB = b.first.frozenDate ?? "";
    return dateA.localeCompare(dateB);
  });
  const virtualCohorts: Specimen[] = draftCohorts.map(
    ({ first, items, thawedCount, fertilizedCount }, index) => {
      // Aggregate usage events from every member so the cohort detail modal
      // shows the full lifecycle history (all thaws, reservations, etc.)
      // instead of just what happened to the first member.
      const aggregatedUsages = items.flatMap(
        (item) => item.specimenUsages ?? [],
      );
      const aggregatedUsageEvents = items.flatMap(
        (item) => item.usageEvents ?? [],
      );
      const aggregatedCycleRelationships = Array.from(
        new Map(
          items
            .flatMap((item) => item.cycleRelationships ?? [])
            .map((relationship) => [
              `${relationship.relation}:${relationship.cycleId}`,
              relationship,
            ]),
        ).values(),
      );
      return {
        ...first,
        id: `${VIRTUAL_OOCYTE_COHORT_ID_PREFIX}${first.cycleId}:${first.frozenDate}`,
        oocyteRecordKind: "cohort",
        numberFrozen: items.length,
        numberThawed: thawedCount,
        fertilizedCount,
        batchId: first.cycleId ?? first.batchId ?? null,
        cohortMemberSpecimenIds: items.map((item) => item.id),
        specimenUsages: aggregatedUsages,
        usageEvents: aggregatedUsageEvents,
        cycleRelationships: aggregatedCycleRelationships,
        // The template `Egg cohort #N` reads through both surfaces that surface
        // this label — the vault tile and the detail-modal title — so numbering
        // here is enough to replace the misleading "Egg #1" spread carried
        // through from the first member.
        label: `Egg cohort #${index + 1}`,
      };
    },
  );
  return { virtualCohorts, memberIdsToRemove };
}

/**
 * Reconstruct picker-ready records from a real oocyte cohort's members. The
 * cohort's members are stored as part of the cohort record, not as rows in
 * `/specimens`, so the picker's default source (`useFrozenSpecimens`) finds
 * nothing to show. This bridge fills the gap with the fields the picker
 * actually reads (facility, collected date, cycle linkage).
 */
function buildOverrideSpecimensFromCohort(
  cohort: OocyteCohort | undefined,
  cohortDetail: CryoSpecimenDetail,
): PickerSpecimen[] {
  if (!cohort) return [];
  return cohort.members
    .filter((member) => member.status === "stored")
    .map((member) => {
      const facility = member.storageFacility ?? cohortDetail.facility ?? "";
      const frozenDate = member.frozenDate ?? cohortDetail.frozenDate ?? "";
      return {
        id: member.id,
        type: "oocyte",
        status: member.status,
        collectedAt: member.collectedAt ?? cohortDetail.retrievalDate ?? null,
        cycleId: cohortDetail.cycleId ?? null,
        notes: null,
        storageLocations: [
          {
            isActive: true,
            storageType: "frozen",
            facility,
            storedFrom: frozenDate,
            storedTo: null,
            notes: null,
          },
        ],
        specimenUsages: [],
      };
    });
}

function NestedSpecimenGrid({
  items,
  onSelect,
}: {
  items: Specimen[];
  onSelect: (id: string) => void;
}) {
  return (
    <View
      style={{
        flexDirection: "row",
        flexWrap: "wrap",
        gap: 8,
        alignItems: "flex-start",
      }}
    >
      {items.map((specimen) => (
        <NestedSpecimenTile
          key={specimen.id}
          specimen={specimen}
          onPress={() => onSelect(specimen.id)}
        />
      ))}
    </View>
  );
}

function GroupedSpecimenGrid({
  items,
  onSelect,
  embedded = false,
}: {
  items: Specimen[];
  onSelect: (id: string) => void;
  /**
   * Inside another card (Vault history): each facility group sits straight in
   * that card instead of drawing a second card inside it.
   */
  embedded?: boolean;
}) {
  const t = useTranslations();
  const { locale } = useLocale();
  const groups = useMemo(() => groupSpecimensByFacilityAndDate(items), [items]);

  return (
    <View style={{ gap: 18 }}>
      {groups.map((group, groupIndex) => {
        const specimenType = group.batches[0]?.items[0]?.type ?? "oocyte";
        // The outer facility/batch header names the intake's total size
        // (the identity of the batch), not what still remains frozen. Each
        // tile's subtitle carries the current state breakdown (frozen ·
        // thawed · fertilized), so showing remaining here would either
        // duplicate that count or read as "0 Eggs" for a fully-consumed
        // batch. Oocytes carry a `numberFrozen` on the cohort, everything
        // else is one row per unit.
        const batchCount = (batch: SpecimenDateBatch) =>
          batch.items.reduce(
            (sum, specimen) =>
              sum +
              (specimen.type === "oocyte"
                ? getOriginalFrozenOocyteCount(specimen)
                : 1),
            0,
          );
        const totalCount = group.batches.reduce(
          (sum, batch) => sum + batchCount(batch),
          0,
        );
        const countLabel = getSpecimenCountLabel(t, specimenType, totalCount);
        const getBatchSubtitle = (batch: SpecimenDateBatch) => {
          const dateLabel = formatSpecimenDate(batch.frozenDate, locale);
          const batchCountLabel = getSpecimenCountLabel(
            t,
            specimenType,
            batchCount(batch),
          );
          return dateLabel
            ? `${batchCountLabel} · ${t(
                "cryo_vault.grouped_inventory.frozen_date",
                {
                  params: { date: dateLabel },
                },
              )}`
            : batchCountLabel;
        };
        const onlyBatch = group.batches.length === 1 ? group.batches[0] : null;
        const facilitySubtitle = onlyBatch
          ? getBatchSubtitle(onlyBatch)
          : countLabel;

        return (
          <View
            key={`${group.facility || "unknown"}-${groupIndex}`}
            style={
              embedded
                ? { gap: 14 }
                : { ...cardSurface(), padding: 14, gap: 14 }
            }
          >
            <View
              style={{
                flexDirection: "row",
                alignItems: "center",
                gap: 10,
                paddingHorizontal: 2,
              }}
            >
              <OrganicMiniIcon kind="clinics" size={34} />
              <View style={{ flex: 1, minWidth: 0 }}>
                <CardTitleText numberOfLines={1}>
                  {group.facility ||
                    t("cryo_vault.grouped_inventory.unknown_facility")}
                </CardTitleText>
                <CardSubtitleText numberOfLines={1}>
                  {facilitySubtitle}
                </CardSubtitleText>
              </View>
            </View>

            <View style={{ gap: 12 }}>
              {group.batches.map((batch) => {
                const batchSubtitle = getBatchSubtitle(batch);
                const showBatchSubtitle = group.batches.length > 1;
                return (
                  <View key={batch.identity} style={{ gap: 8 }}>
                    {showBatchSubtitle ? (
                      <View
                        style={{
                          flexDirection: "row",
                          alignItems: "center",
                          gap: 6,
                          paddingHorizontal: 2,
                        }}
                      >
                        <Calendar
                          size={13}
                          color={EMBER.colors.textLight}
                          strokeWidth={1.6}
                        />
                        <CardSubtitleText
                          numberOfLines={1}
                          style={{ marginTop: 0, fontSize: 12 }}
                        >
                          {batchSubtitle}
                        </CardSubtitleText>
                      </View>
                    ) : null}
                    <NestedSpecimenGrid
                      items={batch.items}
                      onSelect={onSelect}
                    />
                  </View>
                );
              })}
            </View>
          </View>
        );
      })}
    </View>
  );
}

function RetrievalFunnelCard({
  funnel,
  onViewCyclePress,
}: {
  funnel: RetrievalFunnel;
  onViewCyclePress?: () => void;
}) {
  const t = useTranslations();
  type FunnelStep = {
    label: string;
    subtitle: string;
    count: number;
    color: string;
  };

  const steps: FunnelStep[] = [
    {
      label: t("cryo_vault.funnel.retrieved"),
      subtitle: t("cryo_vault.funnel.retrieved_subtitle"),
      count: funnel.retrieved,
      color: "#86EFAC",
    },
  ];

  if (funnel.mature != null) {
    steps.push({
      label: t("cryo_vault.funnel.mature"),
      subtitle: t("cryo_vault.funnel.mature_subtitle"),
      count: funnel.mature,
      color: "#6EE7B7",
    });
  }
  if (funnel.fertilized != null) {
    steps.push({
      label: t("cryo_vault.funnel.fertilized"),
      subtitle: t("cryo_vault.funnel.fertilized_subtitle"),
      count: funnel.fertilized,
      color: "#38BDF8",
    });
  }
  if (funnel.blastocyst != null) {
    steps.push({
      label: t("cryo_vault.funnel.blastocysts"),
      subtitle: t("cryo_vault.funnel.blastocysts_subtitle"),
      count: funnel.blastocyst,
      color: "#818CF8",
    });
  }
  if (funnel.pgtTested != null) {
    steps.push({
      label: t("cryo_vault.funnel.pgt_tested"),
      subtitle: t("cryo_vault.funnel.pgt_tested_subtitle"),
      count: funnel.pgtTested,
      color: "#C084FC",
    });
  }
  if (funnel.euploid != null) {
    steps.push({
      label: t("cryo_vault.funnel.euploid"),
      subtitle: t("cryo_vault.funnel.euploid_subtitle"),
      count: funnel.euploid,
      color: EMBER.colors.green,
    });
  }
  steps.push({
    label: t("cryo_vault.funnel.frozen"),
    subtitle: t("cryo_vault.funnel.frozen_subtitle"),
    count: funnel.frozen,
    color: EMBER.colors.gold,
  });

  const rows: OutcomeDotFunnelStage[] = steps.map((step, index) => ({
    id: `${step.label}-${index}`,
    label: step.label,
    value: step.count,
    detail: step.subtitle,
  }));

  return (
    <View style={{ ...cardSurface(), padding: 16 }}>
      <Text
        className="font-dm-sans"
        style={{
          fontSize: 14,
          fontWeight: "600",
          color: EMBER.colors.text,
          marginBottom: 4,
        }}
      >
        {funnel.cycleLabel}
      </Text>
      <Text
        className="font-dm-sans"
        style={{
          fontSize: 12,
          color: EMBER.colors.textLight,
          lineHeight: 17,
          marginBottom: 18,
        }}
      >
        {t("cryo_vault.funnel.subtitle")}
      </Text>

      <OutcomeDotFunnelRows
        stages={rows}
        totalDots={Math.max(funnel.retrieved, 1)}
        embedded
      />

      {/* View cycle details link */}
      {onViewCyclePress && (
        <TextActionButton
          label={t("cryo_vault.funnel.view_full_cycle")}
          action="cryo_vault.funnel.view_full_cycle"
          onPress={onViewCyclePress}
          size="sm"
          icon={ChevronRight}
          iconPosition="trailing"
          centered
          style={{ marginTop: 12 }}
        />
      )}
    </View>
  );
}

function FacilityCard({ facility }: { facility: FacilityInfo }) {
  const t = useTranslations();
  const countLabel =
    facility.specimenCount === 1
      ? t("cryo_vault.facility.specimen_count_one", {
          params: { count: String(facility.specimenCount) },
        })
      : t("cryo_vault.facility.specimen_count_other", {
          params: { count: String(facility.specimenCount) },
        });

  // TODO: when facility logos become available, render the logo image
  // here instead of the default clinic illustration.
  return (
    <View
      style={{
        flex: 1,
        ...cardSurface(),
        minHeight: 118,
        padding: 14,
        justifyContent: "space-between",
      }}
    >
      <OrganicMiniIcon kind="clinics" size={36} />

      <View style={{ flex: 1, minWidth: 0, marginTop: 12 }}>
        <CardTitleText numberOfLines={1}>{facility.name}</CardTitleText>
        <CardSubtitleText numberOfLines={1}>{countLabel}</CardSubtitleText>
      </View>
    </View>
  );
}

function DispositionCard({
  status,
  onDocumentsPress,
}: {
  status: DispositionStatus;
  onDocumentsPress?: () => void;
}) {
  const capturePress = usePressCapture("DispositionCard");
  const t = useTranslations();
  const isFiled = status === "filed";
  const isExpiring = status === "expiring_soon";

  return (
    <Pressable
      onPress={() => {
        capturePress(
          pressLabel(
            t(
              isFiled
                ? "cryo_vault.disposition.on_file_title"
                : isExpiring
                  ? "cryo_vault.disposition.expiring_title"
                  : "cryo_vault.disposition.missing_title",
            ),
          ),
          "open_specimen_documents",
        );
        onDocumentsPress?.();
      }}
      style={{
        ...cardSurface(),
        flexDirection: "row",
        alignItems: "center",
        gap: 12,
        padding: 14,
      }}
    >
      {isFiled ? (
        <Shield size={16} color={EMBER.colors.green} strokeWidth={1.5} />
      ) : (
        <AlertCircle
          size={16}
          color={isExpiring ? EMBER.colors.gold : EMBER.colors.coral}
          strokeWidth={1.5}
        />
      )}
      <View style={{ flex: 1 }}>
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 13,
            fontWeight: "600",
            color: isFiled
              ? EMBER.colors.green
              : isExpiring
                ? EMBER.colors.gold
                : EMBER.colors.coral,
          }}
        >
          {isFiled
            ? t("cryo_vault.disposition.on_file_title")
            : isExpiring
              ? t("cryo_vault.disposition.expiring_title")
              : t("cryo_vault.disposition.missing_title")}
        </Text>
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 11,
            color: EMBER.colors.textLight,
            marginTop: 2,
            lineHeight: 15,
          }}
        >
          {isFiled
            ? t("cryo_vault.disposition.on_file_subtitle")
            : t("cryo_vault.disposition.missing_subtitle")}
        </Text>
      </View>
      <ChevronRight size={14} color={EMBER.colors.textFaint} />
    </Pressable>
  );
}

export function ActionCard({
  icon,
  label,
  subtitle,
  comingSoon,
  onPress,
}: {
  icon: React.ReactNode;
  label: string;
  subtitle: string;
  comingSoon?: boolean;
  onPress?: () => void;
}) {
  const capturePress = usePressCapture("ActionCard");
  return (
    <Pressable
      onPress={
        comingSoon
          ? undefined
          : () => {
              capturePress(pressLabel(label), "cryo_vault_nav_card");
              onPress?.();
            }
      }
      style={{
        ...cardSurface(),
        flexDirection: "row",
        alignItems: "center",
        gap: 12,
        paddingHorizontal: 15,
        paddingVertical: 13,
        opacity: comingSoon ? 0.65 : 1,
      }}
    >
      <View
        style={{
          width: 38,
          height: 38,
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        {icon}
      </View>
      <View style={{ flex: 1, minWidth: 0 }}>
        <View style={{ flexDirection: "row", alignItems: "center", gap: 6 }}>
          <CardTitleText>{label}</CardTitleText>
          {comingSoon ? <StatusBadge type="coming-soon" /> : null}
        </View>
        <CardSubtitleText>{subtitle}</CardSubtitleText>
      </View>
      {!comingSoon && (
        <ChevronRight
          size={14}
          color={EMBER.colors.textLight}
          strokeWidth={1.8}
        />
      )}
    </Pressable>
  );
}

// The projection is a development-only preview until clinical review defines
// a model with documented inputs, provenance, uncertainty, and presentation.
function isCryoPotentialPreviewEnabled() {
  if (!__DEV__ || typeof window === "undefined") return false;
  return (
    new URLSearchParams(window.location.search).get("dev_cryo_potential") ===
    "true"
  );
}

type PotentialTabId = "combined" | "embryos" | "eggs";

function FertilityPotentialSection({ specimens }: { specimens: Specimen[] }) {
  const t = useTranslations();
  const [activeTab, setActiveTab] = useState<PotentialTabId>("combined");
  if (!isCryoPotentialPreviewEnabled()) return null;

  const euploidEmbryos = specimens.filter(
    (s) => s.type === "embryo" && s.pgtResult === "euploid",
  ).length;
  const untestedEmbryos = specimens.filter(
    (s) =>
      s.type === "embryo" &&
      s.pgtResult !== "euploid" &&
      s.pgtResult !== "aneuploid",
  ).length;
  const frozenEggs = getSpecimenInventoryTotal(
    specimens.filter((s) => s.type === "oocyte"),
  );

  const embryoSteps = computeEmbryoPotential(euploidEmbryos, untestedEmbryos, {
    frozen: t("cryo_vault.potential.frozen_embryos"),
    thaw: t("cryo_vault.potential.survive_thaw"),
    implant: t("cryo_vault.potential.implant_successfully"),
    live: t("cryo_vault.potential.estimated_live_births"),
  });
  const eggSteps = computeEggPotential(frozenEggs, {
    frozen: t("cryo_vault.potential.frozen_eggs"),
    thaw: t("cryo_vault.potential.survive_thaw"),
    fertilize: t("cryo_vault.potential.fertilize"),
    blast: t("cryo_vault.potential.reach_blastocyst"),
    implant: t("cryo_vault.potential.implant_successfully"),
    live: t("cryo_vault.potential.estimated_live_births"),
  });
  const combinedSteps = computeCombinedPotential(embryoSteps, eggSteps);

  const hasEmbryos = embryoSteps.length > 0;
  const hasEggs = eggSteps.length > 0;

  if (!hasEmbryos && !hasEggs) return null;

  const potentialTabOptions: FunnelScopeTabOption<PotentialTabId>[] = [
    {
      id: "combined",
      label: t("cryo_vault.potential.tab_combined"),
      icon: Heart,
    },
    ...(hasEmbryos
      ? [
          {
            id: "embryos" as const,
            label: t("cryo_vault.potential.tab_embryos"),
            icon: Sparkles,
          },
        ]
      : []),
    ...(hasEggs
      ? [
          {
            id: "eggs" as const,
            label: t("cryo_vault.potential.tab_eggs"),
            icon: Egg,
          },
        ]
      : []),
  ];
  const activePotentialTab = potentialTabOptions.some(
    (option) => option.id === activeTab,
  )
    ? activeTab
    : "combined";
  const renderFunnel = (steps: PotentialStep[], type: PotentialTabId) => {
    const rows: OutcomeDotFunnelStage[] = steps.map((step, index) => ({
      id: `${step.label}-${index}`,
      label: step.label,
      value: step.count,
      detail:
        index === 0
          ? t("cryo_vault.potential.stored_cohort_detail")
          : type === "embryos"
            ? ([
                t("cryo_vault.potential.survive_thaw_detail"),
                t("cryo_vault.potential.implant_detail"),
                t("cryo_vault.potential.live_birth_detail"),
              ][index - 1] ?? t("cryo_vault.potential.live_birth_detail"))
            : ([
                t("cryo_vault.potential.survive_thaw_detail"),
                t("cryo_vault.potential.fertilize_detail"),
                t("cryo_vault.potential.reach_blastocyst_detail"),
                t("cryo_vault.potential.implant_detail"),
                t("cryo_vault.potential.live_birth_detail"),
              ][index - 1] ?? t("cryo_vault.potential.live_birth_detail")),
    }));

    return (
      <OutcomeDotFunnelRows
        stages={rows}
        totalDots={Math.max(steps[0]?.count ?? 1, 1)}
        embedded
      />
    );
  };

  const combinedLabelKeys: Record<CombinedPotentialStep["id"], string> = {
    inventory: "cryo_vault.potential.combined_inventory",
    thaw: "cryo_vault.potential.combined_thaw",
    "transfer-ready": "cryo_vault.potential.combined_transfer_ready",
    implant: "cryo_vault.potential.combined_implant",
    live: "cryo_vault.potential.combined_live_births",
  };
  const combinedDetailKeys: Record<CombinedPotentialStep["id"], string> = {
    inventory: "cryo_vault.potential.combined_inventory_detail",
    thaw: "cryo_vault.potential.combined_thaw_detail",
    "transfer-ready": "cryo_vault.potential.combined_transfer_ready_detail",
    implant: "cryo_vault.potential.combined_implant_detail",
    live: "cryo_vault.potential.combined_live_birth_detail",
  };
  const renderCombinedFunnel = (steps: CombinedPotentialStep[]) => {
    const embryoTotal = embryoSteps[0]?.count ?? 0;
    const eggTotal = eggSteps[0]?.count ?? 0;
    const rows: OutcomeDotFunnelStage[] = steps.map((step) => {
      const total = step.embryos + step.eggs;
      const displayValue =
        step.id === "live"
          ? String(total)
          : [step.embryos, step.eggs].filter((count) => count > 0).join("+") ||
            "0";

      return {
        id: `combined-${step.id}`,
        label: t(combinedLabelKeys[step.id]),
        value: total,
        displayValue,
        detail: t(combinedDetailKeys[step.id], {
          params: {
            embryos: String(step.embryos),
            eggs: String(step.eggs),
          },
        }),
        dotSegments: [
          {
            total: embryoTotal,
            active: step.embryos,
            color: SPECIMEN_CONFIG.embryo.color,
          },
          {
            total: eggTotal,
            active: step.eggs,
            color: SPECIMEN_CONFIG.oocyte.color,
          },
        ].filter((segment) => segment.total > 0),
      };
    });

    return (
      <OutcomeDotFunnelRows
        stages={rows}
        totalDots={Math.max(embryoTotal + eggTotal, 1)}
        embedded
      />
    );
  };

  return (
    <View style={{ marginTop: 28 }}>
      <View style={{ marginBottom: 10, paddingHorizontal: 2 }}>
        <SectionTitle icon={Heart}>
          {t("cryo_vault.potential.section_title")}
        </SectionTitle>
      </View>

      <FunnelScopeTabs
        options={potentialTabOptions}
        active={activePotentialTab}
        onChange={setActiveTab}
      />

      {/* The selected scope is the only control; the card starts with its funnel. */}
      <View
        style={{
          ...cardSurface(),
          borderTopLeftRadius: 0,
          borderTopRightRadius: 0,
          borderTopWidth: 0,
          padding: 15,
          marginBottom: 10,
        }}
      >
        <View style={{ gap: 16 }}>
          {activePotentialTab === "combined" ? (
            renderCombinedFunnel(combinedSteps)
          ) : activePotentialTab === "embryos" && hasEmbryos ? (
            <View>
              <View
                style={{
                  flexDirection: "row",
                  alignItems: "center",
                  gap: 6,
                  marginBottom: 12,
                }}
              >
                <View
                  style={{
                    width: 6,
                    height: 6,
                    borderRadius: 3,
                    backgroundColor: SPECIMEN_CONFIG.embryo.color,
                  }}
                />
                <Text
                  className="font-dm-sans"
                  style={{
                    fontSize: 11,
                    fontWeight: "600",
                    color: EMBER.colors.textFaint,
                    textTransform: "uppercase",
                    letterSpacing: 0.5,
                  }}
                >
                  {euploidEmbryos > 0
                    ? t("cryo_vault.potential.from_embryos_with_euploid", {
                        params: { count: String(euploidEmbryos) },
                      })
                    : t("cryo_vault.potential.from_embryos")}
                </Text>
              </View>
              {renderFunnel(embryoSteps, "embryos")}
            </View>
          ) : hasEggs ? (
            <View>
              <View
                style={{
                  flexDirection: "row",
                  alignItems: "center",
                  gap: 6,
                  marginBottom: 12,
                }}
              >
                <View
                  style={{
                    width: 6,
                    height: 6,
                    borderRadius: 3,
                    backgroundColor: SPECIMEN_CONFIG.oocyte.color,
                  }}
                />
                <Text
                  className="font-dm-sans"
                  style={{
                    fontSize: 11,
                    fontWeight: "600",
                    color: EMBER.colors.textFaint,
                    textTransform: "uppercase",
                    letterSpacing: 0.5,
                  }}
                >
                  {t("cryo_vault.potential.from_frozen_eggs")}
                </Text>
              </View>
              {renderFunnel(eggSteps, "eggs")}
            </View>
          ) : null}
        </View>
      </View>
    </View>
  );
}

/** Single glass card with collapsible FAQ items, the same shape as Costs. */
function FAQSection() {
  const t = useTranslations();

  const items = [
    {
      question: t("cryo_vault.faq.expire_question"),
      answer: t("cryo_vault.faq.expire_answer"),
    },
    {
      question: t("cryo_vault.faq.quality_question"),
      answer: t("cryo_vault.faq.quality_answer"),
    },
    {
      question: t("cryo_vault.faq.eggs_needed_question"),
      answer: t("cryo_vault.faq.eggs_needed_answer"),
    },
    {
      question: t("cryo_vault.faq.move_facility_question"),
      answer: t("cryo_vault.faq.move_facility_answer"),
    },
  ];

  return (
    <View
      style={{ ...cardSurface(), paddingHorizontal: 18, paddingVertical: 6 }}
    >
      <Accordion type="single" collapsible>
        {items.map((item, index) => (
          <AccordionItem key={index} value={String(index)}>
            <AccordionTrigger action="toggle_cryo_faq">
              {item.question}
            </AccordionTrigger>
            <AccordionContent>
              <Text
                className="font-dm-sans"
                style={{
                  fontSize: 13,
                  color: EMBER.colors.textLight,
                  lineHeight: 18,
                }}
              >
                {item.answer}
              </Text>
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </View>
  );
}

function revokeCryoUploadObjectUrl(file: CryoUploadFile | null) {
  if (Platform.OS !== "web" || !file?.objectUrl || typeof URL === "undefined") {
    return;
  }
  URL.revokeObjectURL(file.objectUrl);
}

function EmptyVault({
  onUpload,
  onFileSelected,
}: {
  onUpload: () => void;
  onFileSelected: (file: CryoUploadFile) => void;
}) {
  const t = useTranslations();
  // Reuse the approved homepage crop, scaled to a compact 72px visible height.
  const scale = 72 / 552;
  return (
    <View style={{ paddingVertical: 8, alignItems: "center" }}>
      <DocumentFileDropZone
        onPick={onUpload}
        onFileSelected={onFileSelected}
        singleFile
        dropHint={t("cryo_vault.empty.drop_one_report_here")}
      >
        <EmptyState
          density="compact"
          titleVariant="featured"
          illustration={
            <View
              testID="vault-empty-illustration"
              style={{ width: 463 * scale, height: 72, overflow: "hidden" }}
            >
              <Image
                source={vaultIllustration}
                resizeMode="contain"
                accessible={false}
                style={{
                  position: "absolute",
                  width: 600 * scale,
                  height: 631 * scale,
                  left: -137 * scale,
                  top: -79 * scale,
                }}
              />
            </View>
          }
          title={t("cryo_vault.empty.add_any_frozen_specimen")}
          subtitle={t("cryo_vault.empty.store_your_frozen_specimens")}
          subtitleStyle={{ maxWidth: 410 }}
          style={{ paddingVertical: 0 }}
          action={
            <CompactActionButton
              label={t("cryo_vault.empty.add_specimens")}
              action="cryo_vault.empty.add_specimens"
              leadingIcon={Plus}
              variant="primary"
              onPress={onUpload}
            />
          }
        />
      </DocumentFileDropZone>
    </View>
  );
}

function EmbryoTransferSelectionDialog({
  visible,
  embryos,
  onClose,
  onContinue,
}: {
  visible: boolean;
  embryos: Specimen[];
  onClose: () => void;
  onContinue: (selected: Specimen[]) => void;
}) {
  const t = useTranslations();
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set());

  useEffect(() => {
    if (visible) setSelectedIds(new Set());
  }, [visible]);

  const selected = embryos.filter((embryo) => selectedIds.has(embryo.id));

  return (
    <Dialog
      visible={visible}
      onClose={onClose}
      layout="centered"
      closeButton="minimal"
      surfaceStyle={{ maxHeight: 720 }}
      contentStyle={{ paddingHorizontal: 20, paddingBottom: 18 }}
      testID="embryo-transfer-selection-dialog"
    >
      <DialogHeader
        title={t(
          "cryo_vault.transfer_selection.choose_embryos_for_this_transfer",
        )}
        subtitle={t(
          "cryo_vault.transfer_selection.select_records_to_review_with_clinic",
        )}
        style={{ marginBottom: 14 }}
      />
      <ScrollView
        showsVerticalScrollIndicator={false}
        style={{ maxHeight: 430 }}
        contentContainerStyle={{ gap: 8, paddingBottom: 4 }}
      >
        {embryos.map((embryo) => {
          const isSelected = selectedIds.has(embryo.id);
          // The canonical Row with the shared checkbox; the whole row is the
          // control, so the checkbox is the box alone. Input fill: a glass
          // row's shadow is clipped by the dialog's scroll area.
          return (
            <Row
              key={embryo.id}
              surface="input"
              // Dense, matching the egg picker.
              density="dense"
              paddingVertical={10}
              leading={<MoonCheckboxBox checked={isSelected} />}
              leadingSize={24}
              title={embryo.label}
              titleNumberOfLines={1}
              subtitle={[embryo.facility, embryo.cycleLabel, embryo.pgtResult]
                .filter(Boolean)
                .join(" · ")}
              subtitleNumberOfLines={2}
              chevron={false}
              accessibilityRole="checkbox"
              accessibilityState={{ checked: isSelected }}
              accessibilityLabel={embryo.label}
              action="select_embryo"
              onPress={() =>
                setSelectedIds((current) => {
                  const next = new Set(current);
                  if (next.has(embryo.id)) next.delete(embryo.id);
                  else next.add(embryo.id);
                  return next;
                })
              }
            />
          );
        })}
      </ScrollView>
      <View style={{ flexDirection: "row", gap: 10, marginTop: 16 }}>
        <SecondaryButton
          label={t("cryo_vault.transfer_selection.cancel")}
          onPress={onClose}
          flex={0.8}
        />
        <PrimaryButton
          label={t("cryo_vault.transfer_selection.review_selection")}
          onPress={() => onContinue(selected)}
          disabled={selected.length === 0}
          flex={1.2}
        />
      </View>
    </Dialog>
  );
}

// ── Screen ──

type Props = NativeStackScreenProps<CareStackParamList, "CryoVault">;

export default function CryoVaultScreen({ navigation, route }: Props) {
  const handleBack = useSmartBack({
    fallbackTab: "Care",
    fallbackScreen: "CareOverview",
  });
  const t = useTranslations();
  const { locale } = useLocale();
  const devCryoVaultScenario = getDevCryoVaultScenario();
  const shouldOpenAddEmbryosSurface = shouldOpenDevAddEmbryosSurface();
  const specimensQuery = useSpecimens();
  const deleteSpecimen = useDeleteSpecimen();
  const {
    data: apiSpecimens,
    isLoading: specimensLoading,
    error: specimensError,
  } = specimensQuery;
  const cohortsQuery = useOocyteCohorts();
  const {
    data: apiOocyteCohorts,
    isLoading: cohortsLoading,
    error: cohortsError,
  } = cohortsQuery;
  const cyclesQuery = useCycles();
  const { data: apiCycles } = cyclesQuery;
  const documentsQuery = useDocuments();
  const {
    data: documents,
    isLoading: documentsLoading,
    error: documentsError,
  } = documentsQuery;
  const devClinicalDataState = getDevClinicalDataState("cryo");
  const coreCryoError = specimensError ?? cohortsError;
  const hasResolvedCryoSource =
    apiSpecimens !== undefined || apiOocyteCohorts !== undefined;
  const cryoDataState = devClinicalDataState
    ? devClinicalDataState
    : devCryoVaultScenario
      ? null
      : coreCryoError && !hasResolvedCryoSource
        ? "unavailable"
        : coreCryoError || documentsError
          ? "stale"
          : null;
  const isRetryingCryo =
    specimensQuery.isFetching ||
    cohortsQuery.isFetching ||
    documentsQuery.isFetching;
  const retryCryo = () => {
    void specimensQuery.refetch();
    void cohortsQuery.refetch();
    void documentsQuery.refetch();
  };
  const specimens = useMemo(() => {
    const buildLabel = (apiType: string, n: number) => {
      return buildCryoSpecimenLabel(t, apiType, n);
    };

    if (devCryoVaultScenario === "oocyte_batches") {
      return buildDevCryoVaultSpecimens(buildLabel);
    }
    if (devCryoVaultScenario === "specimen_modal") {
      return buildDevSpecimenModalSpecimens(buildLabel);
    }

    // Fertilized count for a cohort is the sum of `fertilized_oocytes`
    // metrics on every cycle that consumed at least one of the cohort's
    // members. Reading from the phase metric (the Lab funnel's source)
    // rather than the specimen event stream avoids the deprecated
    // material-operations write path for `fertilize`/`use`.
    const fertilizedByCycle = new Map<string, number>();
    const cyclesByMemberId = new Map<string, Set<string>>();
    for (const cycle of apiCycles ?? []) {
      let cycleFertilized = 0;
      for (const phase of cycle.phases ?? []) {
        if (phase.type !== "fertilization") continue;
        for (const metric of phase.fertilityMetrics ?? []) {
          if (metric.type === "fertilized_oocytes" && metric.value != null) {
            cycleFertilized += metric.value;
          }
        }
      }
      if (cycleFertilized > 0) {
        fertilizedByCycle.set(cycle.id, cycleFertilized);
      }
      for (const memberId of cycle.contextSnapshot?.materialIds ?? []) {
        let set = cyclesByMemberId.get(memberId);
        if (!set) {
          set = new Set<string>();
          cyclesByMemberId.set(memberId, set);
        }
        set.add(cycle.id);
      }
    }
    const getFertilizedForMembers = (memberIds: readonly string[]): number => {
      const seenCycles = new Set<string>();
      for (const memberId of memberIds) {
        const cycles = cyclesByMemberId.get(memberId);
        if (!cycles) continue;
        for (const cid of cycles) seenCycles.add(cid);
      }
      let total = 0;
      for (const cid of seenCycles) total += fertilizedByCycle.get(cid) ?? 0;
      return total;
    };

    const cohorts = mapApiOocyteCohortsToCryoDetails(apiOocyteCohorts ?? []);
    const cohortsWithFertilized = cohorts.map((cohort) => {
      const memberIds = cohort.cohortMemberSpecimenIds ?? [];
      const derived = getFertilizedForMembers(memberIds);
      // Prefer the cohort record's own count if the backend keeps one on
      // hand; the derived total is the fallback for cohorts the backend
      // has not aggregated yet.
      if (cohort.fertilizedCount != null && cohort.fertilizedCount > 0) {
        return cohort;
      }
      return derived > 0 ? { ...cohort, fertilizedCount: derived } : cohort;
    });
    const cohortMemberIds = new Set(
      (apiOocyteCohorts ?? []).flatMap((cohort) =>
        cohort.members.map((member) => member.id),
      ),
    );
    const rawIndividuals = (apiSpecimens ?? []).filter(
      (specimen) => !cohortMemberIds.has(specimen.id),
    );
    const mappedIndividuals = mapApiSpecimensToCryoDetails(
      rawIndividuals,
      buildLabel,
    );
    // Same-retrieval individual oocytes collapse into a synthetic cohort so
    // the vault, the detail modal, and the "use eggs" flow all treat one
    // intake as one thing. The individuals themselves are dropped from the
    // vault-side list because they are now represented by the synthetic; the
    // real specimen IDs live on the synthetic's `cohortMemberSpecimenIds`
    // for the picker to resolve.
    const { virtualCohorts, memberIdsToRemove } = buildVirtualOocyteCohorts(
      mappedIndividuals,
      getFertilizedForMembers,
    );
    const survivingIndividuals = mappedIndividuals.filter(
      (specimen) => !memberIdsToRemove.has(specimen.id),
    );
    return [
      ...survivingIndividuals,
      ...cohortsWithFertilized,
      ...virtualCohorts,
    ];
  }, [apiCycles, apiOocyteCohorts, apiSpecimens, devCryoVaultScenario, t]);
  const displaySpecimensLoading = devCryoVaultScenario
    ? false
    : specimensLoading || cohortsLoading;
  const displayDocumentsLoading = devCryoVaultScenario
    ? false
    : documentsLoading;
  const cryoDocumentRelationTags = useMemo(
    () => getCryoDocumentRelationTags(specimens),
    [specimens],
  );
  const cryoDocuments = useMemo(
    () =>
      (documents ?? []).filter((document) =>
        isCryoRelatedDocument(document, cryoDocumentRelationTags),
      ),
    [cryoDocumentRelationTags, documents],
  );

  const facilities = useMemo((): FacilityInfo[] => {
    const map = new Map<string, FacilityInfo>();
    for (const s of specimens.filter(isVerifiedStoredSpecimen)) {
      if (!s.facility) continue;
      const existing = map.get(s.facility);
      if (existing) {
        existing.specimenCount++;
        if (
          s.frozenDate &&
          (!existing.storageStartDate ||
            s.frozenDate < existing.storageStartDate)
        ) {
          existing.storageStartDate = s.frozenDate;
        }
      } else {
        map.set(s.facility, {
          name: s.facility,
          specimenCount: 1,
          storageStartDate: s.frozenDate,
        });
      }
    }
    return Array.from(map.values());
  }, [specimens]);

  const funnels: RetrievalFunnel[] = [];
  const dispositionStatus: DispositionStatus | null = null;

  const [selectedSpecimen, setSelectedSpecimen] = useState<string | null>(
    route.params?.specimenId ?? null,
  );
  const [addSpecimenVisible, setAddSpecimenVisible] = useState(false);
  const [pendingSpecimenFile, setPendingSpecimenFile] =
    useState<CryoUploadFile | null>(null);
  const [transferSelectionVisible, setTransferSelectionVisible] =
    useState(false);
  /**
   * When she picks "Use eggs" on any oocyte cohort — real or virtual — open a
   * picker scoped to just those members, so she can pick the subset she
   * actually wants to thaw. Real cycles often use only a few eggs from a
   * cohort. Opening the picker does NOT start a cycle: the open-cycle check
   * only fires when she confirms a selection and enters the treatment start
   * flow.
   *
   * For a virtual cohort, `overrideSpecimens` stays null: the members exist
   * in `useFrozenSpecimens` and the picker resolves them itself. For a real
   * cohort, members are stored as part of the cohort record and are not
   * addressable through the specimens query — we hand the picker a
   * reconstructed list built from `cohort.members`.
   */
  const [oocyteCohortPicker, setOocyteCohortPicker] = useState<{
    specimenIds: string[];
    overrideSpecimens?: PickerSpecimen[];
  } | null>(null);
  const { openStartTreatment: openStartTreatmentModal } =
    useStartTreatmentController();
  const resolveCycleNavigation = useCycleNavigation();

  useEffect(() => {
    if (shouldOpenAddEmbryosSurface) {
      setAddSpecimenVisible(true);
    }
  }, [devCryoVaultScenario, shouldOpenAddEmbryosSurface]);

  useEffect(() => {
    if (route.params?.specimenId) {
      setSelectedSpecimen(route.params.specimenId);
    }
  }, [route.params?.specimenId]);

  const handleCloseSpecimen = () => {
    setSelectedSpecimen(null);
    if (route.params?.specimenId) {
      navigation.setParams({ specimenId: undefined });
    }
  };

  const handleDeleteSpecimen = async (specimen: CryoSpecimenDetail) => {
    // A virtual cohort has no server-side row of its own — deleting it would
    // 404. To retire an intake she recorded through the app, she has to
    // delete its members individually, which the detail modal offers when
    // she opens one from the picker.
    if (isVirtualOocyteCohortId(specimen.id)) {
      handleCloseSpecimen();
      return;
    }
    await deleteSpecimen.mutateAsync(specimen.id);
    handleCloseSpecimen();
  };

  const verifiedSpecimens = specimens.filter(isVerifiedStoredSpecimen);
  // Material that has left frozen storage drops into Vault history: a
  // specimen the backend records as used, an oocyte cohort whose whole count
  // has been thawed, or any other row with a physically-consumed event.
  // Partially used cohorts that still have eggs left stay in the main
  // sections. History rows are dimmed and excluded from the vault total.
  const isSpecimenInVaultHistory = (specimen: Specimen): boolean => {
    if (specimen.status === "used") return true;
    if (specimen.type === "oocyte") {
      const total = getOriginalFrozenOocyteCount(specimen);
      if (total <= 0) return false;
      return getRemainingFrozenOocyteCount(specimen) === 0;
    }
    return (specimen.specimenUsages ?? []).some((usage) =>
      ["thaw", "use", "transfer", "fertilize"].includes(usage.usage),
    );
  };
  const availableSpecimens = verifiedSpecimens.filter(
    (s) => !isSpecimenInVaultHistory(s),
  );
  // `isVerifiedStoredSpecimen` requires status "stored", so a specimen the
  // backend has marked used would otherwise vanish from both lists.
  const historySpecimens = specimens.filter(
    (specimen) =>
      (specimen.status === "used" || isVerifiedStoredSpecimen(specimen)) &&
      isSpecimenInVaultHistory(specimen),
  );
  const embryos = availableSpecimens.filter((s) => s.type === "embryo");
  const eggs = availableSpecimens.filter((s) => s.type === "oocyte");
  const sperm = availableSpecimens.filter((s) => s.type === "sperm");

  const hasEmbryos = embryos.length > 0;
  const hasEggs = eggs.length > 0;

  const sections = [
    embryos.length > 0 && { type: "embryo" as SpecimenType, items: embryos },
    eggs.length > 0 && { type: "oocyte" as SpecimenType, items: eggs },
    sperm.length > 0 && { type: "sperm" as SpecimenType, items: sperm },
  ].filter(Boolean) as { type: SpecimenType; items: Specimen[] }[];

  const handleDocumentsPress = () => {
    navigation.navigate("Documents", { initialCategory: "legal_consent" });
  };

  const handleCryoDocumentUpload = () => {
    navigation.navigate("Documents", { openUpload: true });
  };

  const openAddSpecimen = useCallback(() => {
    setPendingSpecimenFile(null);
    setAddSpecimenVisible(true);
  }, []);

  const openAddSpecimenWithFile = useCallback((file: CryoUploadFile) => {
    setPendingSpecimenFile((previous) => {
      revokeCryoUploadObjectUrl(previous);
      return file;
    });
    setAddSpecimenVisible(true);
  }, []);

  const closeAddSpecimen = useCallback(() => {
    setAddSpecimenVisible(false);
    setPendingSpecimenFile((previous) => {
      revokeCryoUploadObjectUrl(previous);
      return null;
    });
  }, []);

  const openStartTreatment = (frozenPrefill: FrozenPrefill | null = null) => {
    openStartTreatmentModal({
      frozenPrefill,
      source: "CryoVaultScreen",
      // Replace so the back button lands on CareOverview, not on the empty
      // CryoVault the user just left.
      onCreated: (createdCycle) =>
        navigation.replace("CycleDetail", { cycleId: createdCycle?.id }),
      onOpenActiveCycle: (cycle) =>
        navigation.replace("CycleDetail", { cycleId: cycle.id }),
    });
  };

  const handleEmbryoTransferSelection = (selected: Specimen[]) => {
    setTransferSelectionVisible(false);
    openStartTreatment({
      type: "embryo",
      specimenIds: selected.map((specimen) => specimen.id),
      specimenLabels: selected.map((specimen) => specimen.label),
      specimens: selected,
    });
  };

  return (
    <GlassScreenLayout>
      <ScrollView
        contentContainerStyle={{
          paddingHorizontal: 16,
          paddingTop: SCREEN_PADDING_TOP,
          paddingBottom: 100,
          maxWidth: 520,
          width: "100%",
          alignSelf: "center",
        }}
        showsVerticalScrollIndicator={false}
      >
        {/* Back */}
        <BackLink
          label={t("cryo_vault.back")}
          action="cryo_vault.back"
          onPress={handleBack}
          style={{ marginBottom: 16 }}
        />

        <ScreenHeader
          title={t("cryo_vault.title")}
          subtitle={t("cryo_vault.subtitle")}
          right={
            cryoDataState === "unavailable" ? undefined : (
              <IconButton
                accessibilityLabel="Add specimen"
                action="open_add_specimen"
                onPress={openAddSpecimen}
                size="lg"
                backgroundColor={`${EMBER.colors.warm}14`}
                pressedBackgroundColor={`${EMBER.colors.warm}14`}
                pressedOpacity={1}
              >
                <Plus size={18} color={EMBER.colors.warm} strokeWidth={2} />
              </IconButton>
            )
          }
        />

        {cryoDataState === "stale" ? (
          <ClinicalDataStatus
            state="stale"
            message={t("clinical_data.stale_body")}
            retryLabel={t("clinical_data.retry")}
            retryingLabel={t("clinical_data.retrying")}
            onRetry={retryCryo}
            isRetrying={isRetryingCryo}
            style={{ marginBottom: 12 }}
            testID="cryo-vault-stale"
          />
        ) : null}

        {cryoDataState === "unavailable" ? (
          <ClinicalDataStatus
            state="unavailable"
            title={t("clinical_data.unavailable_title")}
            message={t("clinical_data.unavailable_body")}
            retryLabel={t("clinical_data.retry")}
            retryingLabel={t("clinical_data.retrying")}
            onRetry={retryCryo}
            isRetrying={isRetryingCryo}
            testID="cryo-vault-unavailable"
          />
        ) : displaySpecimensLoading ? (
          <View testID="cryo-vault-loading" style={{ paddingVertical: 8 }}>
            {[0, 1, 2].map((row) => (
              <ListRowSkeleton key={row} showLeading showSubtitle />
            ))}
          </View>
        ) : specimens.length === 0 ? (
          <EmptyVault
            onUpload={openAddSpecimen}
            onFileSelected={openAddSpecimenWithFile}
          />
        ) : (
          <>
            {/* Summary — the total counts current inventory only; used
                cohorts and consumed specimens live under "Vault history"
                below and are excluded here. */}
            <CryoSummary specimens={availableSpecimens} />

            {/* Specimen sections as tile grids */}
            {sections.map((section) => {
              const config = SPECIMEN_CONFIG[section.type];
              const sectionCount = getSpecimenInventoryTotal(section.items);
              const hasFacilityDetails = section.items.some((item) =>
                item.facility.trim(),
              );
              return (
                <View key={section.type} style={{ marginTop: 24 }}>
                  <View style={{ marginBottom: 10, paddingHorizontal: 2 }}>
                    <SectionTitle icon={config.sectionIcon}>
                      {`${sectionCount} ${
                        sectionCount === 1
                          ? t(config.singularKey)
                          : t(config.pluralKey)
                      }`}
                    </SectionTitle>
                  </View>
                  {hasFacilityDetails ? (
                    <GroupedSpecimenGrid
                      items={section.items}
                      onSelect={(id) => setSelectedSpecimen(id)}
                    />
                  ) : (
                    <SpecimenGrid
                      items={section.items}
                      onSelect={(id) => setSelectedSpecimen(id)}
                    />
                  )}
                </View>
              );
            })}

            {/* Vault history — cohorts and specimens that have been
                fully consumed live here, in a self-contained glass card
                that mirrors the `PastCyclesCarousel` shape (shared glass
                card, inner header + tiles) so the section reads as its own
                bounded surface rather than a continuation of the current
                inventory grids. The card boundary is the "this is past"
                signal, so the outer dim is dropped; the vertical scroll
                inside keeps the section from dominating the page and the
                custom warm-toned bar makes the affordance visible before
                the user touches it. Sort by the latest consuming event
                so recent activity surfaces first. */}
            {historySpecimens.length > 0 &&
              (() => {
                const latestConsumingEventAt = (specimen: Specimen): string => {
                  const events = specimen.usageEvents ?? [];
                  let latest = "";
                  for (const event of events) {
                    if (!CONSUMING_SPECIMEN_USAGES.has(event.usage)) continue;
                    const timestamp = `${event.date ?? ""}T${event.time ?? ""}`;
                    if (timestamp.localeCompare(latest) > 0) {
                      latest = timestamp;
                    }
                  }
                  return latest || specimen.frozenDate || "";
                };
                const sortedUsed = [...historySpecimens].sort((a, b) =>
                  latestConsumingEventAt(b).localeCompare(
                    latestConsumingEventAt(a),
                  ),
                );
                return (
                  <View
                    style={{
                      ...cardSurface(),
                      marginTop: 24,
                      padding: 15,
                      gap: 12,
                    }}
                  >
                    <View
                      style={{
                        flexDirection: "row",
                        alignItems: "flex-start",
                        gap: 10,
                      }}
                    >
                      <View
                        style={{
                          width: 38,
                          height: 38,
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                        }}
                      >
                        <OrganicMiniIcon kind="clinics" size={36} />
                      </View>
                      <View style={{ flex: 1, minWidth: 0 }}>
                        <CardTitleText>
                          {t("cryo_vault.sections.vault_history")}
                        </CardTitleText>
                        <CardSubtitleText>
                          {t("cryo_vault.sections.vault_history_subtitle")}
                        </CardSubtitleText>
                      </View>
                    </View>
                    <ScrollView
                      style={{ maxHeight: 520 }}
                      nestedScrollEnabled
                      showsVerticalScrollIndicator={false}
                      // Only the tiles read as inactive — the card's header
                      // stays crisp so the section itself is easy to scan.
                      contentContainerStyle={{ opacity: 0.7 }}
                    >
                      <GroupedSpecimenGrid
                        items={sortedUsed}
                        onSelect={(id) => setSelectedSpecimen(id)}
                        embedded
                      />
                    </ScrollView>
                  </View>
                );
              })()}

            {/* Fertility potential — cryo to live birth projection.
                Reads from available (still-frozen) material only, so a
                fully-consumed cohort under Vault History does not skew a
                projection about what she still has to work with. */}
            <FertilityPotentialSection specimens={availableSpecimens} />

            {/* Actions */}
            <View style={{ marginTop: 28 }}>
              <View style={{ marginBottom: 10, paddingHorizontal: 2 }}>
                <SectionTitle icon={Sparkles}>
                  {t("cryo_vault.sections.what_to_do")}
                </SectionTitle>
              </View>
              <View style={{ gap: 8 }}>
                {hasEmbryos && (
                  <ActionCard
                    icon={<OrganicMiniIcon kind="genetics" size={36} />}
                    label={t("cryo_vault.actions.plan_transfer_label")}
                    subtitle={t("cryo_vault.actions.plan_transfer_subtitle")}
                    onPress={() => setTransferSelectionVisible(true)}
                  />
                )}
                {hasEggs && (
                  <ActionCard
                    icon={<OrganicMiniIcon kind="eggs" size={36} />}
                    label={t("cryo_vault.actions.fertilize_label")}
                    subtitle={t("cryo_vault.actions.fertilize_subtitle")}
                    onPress={() => {
                      const eggIds = eggs.map((e) => e.id);
                      openStartTreatment({
                        type: "oocyte",
                        specimenIds: eggIds,
                        specimens: eggs,
                      });
                    }}
                  />
                )}
                <ActionCard
                  icon={<OrganicMiniIcon kind="start" size={36} />}
                  label={t("cryo_vault.actions.start_cycle_label")}
                  subtitle={t("cryo_vault.actions.start_cycle_subtitle")}
                  onPress={() => openStartTreatment()}
                />
                <ActionCard
                  icon={
                    <Truck
                      size={22}
                      color={EMBER.colors.textLight}
                      strokeWidth={1.5}
                    />
                  }
                  label={t("cryo_vault.actions.move_label")}
                  subtitle={t("cryo_vault.actions.move_subtitle")}
                  comingSoon
                />
              </View>
            </View>

            {/* Retrieval funnels */}
            {funnels.length > 0 && (
              <View style={{ marginTop: 28 }}>
                <Text
                  className="font-dm-sans"
                  style={{
                    fontSize: 16,
                    fontWeight: "700",
                    color: EMBER.colors.text,
                    marginBottom: 10,
                    paddingHorizontal: 2,
                  }}
                >
                  {t("cryo_vault.sections.retrieval_results")}
                </Text>
                <View style={{ gap: 10 }}>
                  {funnels.map((f) => (
                    <RetrievalFunnelCard
                      key={f.cycleLabel}
                      funnel={f}
                      onViewCyclePress={() => {
                        if (f.cycleId) {
                          navigation.navigate("PastCycleDetail", {
                            cycleId: f.cycleId,
                          });
                        }
                      }}
                    />
                  ))}
                </View>
              </View>
            )}

            {/* Facilities */}
            {facilities.length > 0 && (
              <View style={{ marginTop: 28 }}>
                <View style={{ marginBottom: 10, paddingHorizontal: 2 }}>
                  <SectionTitle icon={MapPin}>
                    {t("cryo_vault.sections.storage_facilities")}
                  </SectionTitle>
                </View>
                <View
                  style={{
                    flexDirection: "row",
                    flexWrap: "wrap",
                    gap: 10,
                  }}
                >
                  {facilities.map((f) => (
                    <View
                      key={f.name}
                      style={{
                        flexGrow: 1,
                        flexBasis: "22%",
                        minWidth: 130,
                      }}
                    >
                      <FacilityCard facility={f} />
                    </View>
                  ))}
                </View>
              </View>
            )}

            {/* Disposition */}
            {dispositionStatus && (
              <View style={{ marginTop: 24 }}>
                <DispositionCard
                  status={dispositionStatus}
                  onDocumentsPress={handleDocumentsPress}
                />
              </View>
            )}

            {/* Related documents */}
            <View style={{ marginTop: 28 }}>
              <View style={{ marginBottom: 10, paddingHorizontal: 2 }}>
                <SectionTitle icon={FileText}>
                  {t("cryo_vault.sections.related_documents")}
                </SectionTitle>
              </View>
              {displayDocumentsLoading ? (
                <View>
                  {Array.from({ length: 2 }).map((_, index) => (
                    <ListRowSkeleton
                      key={index}
                      showLeadingSquare
                      showSubtitle
                      showTrailing
                    />
                  ))}
                </View>
              ) : cryoDocuments.length > 0 ? (
                <View style={{ gap: 8 }}>
                  {cryoDocuments.map((document) => (
                    <DocumentRow
                      key={document.id}
                      title={document.name}
                      dateLabel={
                        formatSpecimenDate(
                          document.documentDate ?? document.createdAt,
                          locale,
                        ) ?? undefined
                      }
                      icon={
                        <FileText
                          size={18}
                          color={EMBER.colors.textLight}
                          strokeWidth={1.5}
                        />
                      }
                      status={getDocumentRowStatus(document)}
                      onPress={() =>
                        navigation.navigate("Documents", {
                          initialDocumentId: document.id,
                        })
                      }
                    />
                  ))}
                </View>
              ) : (
                <DocumentRow
                  title={t("care.cycle_documents.empty_title")}
                  subtitle={t("care.cycle_documents.empty_subtitle")}
                  icon={<OrganicMiniIcon kind="medical-records" size={34} />}
                  trailing={
                    <Plus
                      size={18}
                      color={EMBER.colors.textLight}
                      strokeWidth={1.5}
                    />
                  }
                  onPress={handleCryoDocumentUpload}
                  showChevron={false}
                />
              )}
            </View>

            {/* FAQ */}
            <View style={{ marginTop: 28, marginBottom: 8 }}>
              <View style={{ marginBottom: 10, paddingHorizontal: 2 }}>
                <SectionTitle icon={Info}>
                  {t("cryo_vault.sections.common_questions")}
                </SectionTitle>
              </View>
              <FAQSection />
            </View>
          </>
        )}
      </ScrollView>

      <CryoSpecimenDetailModal
        specimen={specimens.find((s) => s.id === selectedSpecimen) ?? null}
        allSpecimens={specimens}
        visible={selectedSpecimen != null}
        onClose={handleCloseSpecimen}
        onSelectSpecimen={setSelectedSpecimen}
        onDelete={handleDeleteSpecimen}
        onPlanTreatment={(specimen) => {
          if (
            specimen.type !== "sperm" &&
            specimen.type !== "embryo" &&
            specimen.type !== "oocyte"
          ) {
            return;
          }
          const isOocyteCohort =
            specimen.type === "oocyte" &&
            specimen.oocyteRecordKind === "cohort" &&
            (specimen.cohortMemberSpecimenIds?.length ?? 0) > 0;
          if (isOocyteCohort) {
            const memberIds = specimen.cohortMemberSpecimenIds ?? [];
            const isVirtual = isVirtualOocyteCohortId(specimen.id);
            if (isVirtual) {
              // Virtual cohort members ARE in the frozen specimens query;
              // the picker narrows this historical member list to those
              // currently available in storage.
              handleCloseSpecimen();
              setOocyteCohortPicker({ specimenIds: memberIds });
              return;
            }
            // Real cohort members live only on the cohort record. Reconstruct
            // minimal Specimen shapes from the source API cohort so the
            // picker can render them without hitting `useFrozenSpecimens`.
            const sourceCohort = (apiOocyteCohorts ?? []).find(
              (cohort) => cohort.id === specimen.id,
            );
            const overrideSpecimens = buildOverrideSpecimensFromCohort(
              sourceCohort,
              specimen,
            );
            handleCloseSpecimen();
            setOocyteCohortPicker({
              specimenIds: overrideSpecimens.map((member) => member.id),
              overrideSpecimens,
            });
            return;
          }
          handleCloseSpecimen();
          openStartTreatment({
            type: specimen.type === "sperm" ? "semen" : specimen.type,
            specimenIds: [specimen.id],
            specimenLabels: [specimen.label],
            specimens: [specimen],
          });
        }}
        getCycleNavigation={(cycleId) => {
          const navigate = resolveCycleNavigation(cycleId);
          if (!navigate) return undefined;
          return () => {
            handleCloseSpecimen();
            navigate();
          };
        }}
        getEmbryoReportNavigation={(cycleId) => () => {
          handleCloseSpecimen();
          navigation.navigate("EmbryoReport", { cycleId });
        }}
      />
      <EmbryoTransferSelectionDialog
        visible={transferSelectionVisible}
        embryos={embryos}
        onClose={() => setTransferSelectionVisible(false)}
        onContinue={handleEmbryoTransferSelection}
      />
      <SelectFrozenSpecimensModal
        visible={oocyteCohortPicker !== null}
        type="oocyte"
        initialSpecimenIds={oocyteCohortPicker?.specimenIds ?? []}
        scopedSpecimenIds={oocyteCohortPicker?.specimenIds}
        overrideSpecimens={oocyteCohortPicker?.overrideSpecimens}
        onBack={() => setOocyteCohortPicker(null)}
        onConfirm={(selection) => {
          const chosenIds = selection.specimenIds;
          const cohortSpecimens = oocyteCohortPicker?.overrideSpecimens?.map(
            (member) => ({
              id: member.id,
              collectedAt: member.collectedAt,
              facility:
                member.storageLocations.find(
                  (l) => l.isActive && l.storageType === "frozen",
                )?.facility ?? null,
            }),
          );
          setOocyteCohortPicker(null);
          if (chosenIds.length === 0) return;
          // Labels are optional on the treatment prefill (the picker in
          // MoonStartTreatmentModal renders its own count-only summary when
          // labels are absent) and would require another lookup here.
          openStartTreatment({
            type: "oocyte",
            specimenIds: chosenIds,
            specimens: cohortSpecimens,
          });
        }}
      />
      <AddSpecimenModal
        visible={addSpecimenVisible}
        onClose={closeAddSpecimen}
        prefill={getDevAddEmbryosPrefill()}
        initialFile={pendingSpecimenFile}
      />
    </GlassScreenLayout>
  );
}
